// src/components/defined/specification_builder/SpecificationBuilderTab.jsx

import React, { useEffect, useMemo, useState } from "react";
import theme from "../../../styles/theme";

import BuildingSystemsSymbol from "./BuildingSystemsSymbol";
import TechnicalSystemsSymbol from "./TechnicalSystemsSymbol";
import SpecificAssetsSymbol from "./SpecificAssetsSymbol";

import Button from "../../reusable/Forms/Button";
import DropdownList  from "../../reusable/Forms/DropdownList";
import TextFilter  from "../../reusable/Forms/TextFilter";
import RadioGroup from "../../reusable/Forms/RadioGroup";
import PinRepere from "../../reusable/Forms/PinRepere";

/* ============================================================================
   SPECIFICATION BUILDER — ZONE C1
   ----------------------------------------------------------------------------
   RÔLE
   - naviguer dans le corpus de spécifications par librairie technique
   - sélectionner un système puis un article
   - éditer / créer / dupliquer une spécification
   - conserver une interface plus aérée et praticienne
============================================================================ */

/* ============================================================================
   TOKENS
============================================================================ */

const T = {
  bg: theme?.colors?.background?.surface || "#ffffff",
  panel: theme?.colors?.background?.panel || "#f1f3f6",
  card: theme?.colors?.background?.Card || "#f9f9fa",
  hover: theme?.colors?.background?.hover || "#f7f9fc",

  border: theme?.colors?.borders?.default || "#d6dee4",
  borderPanel: theme?.colors?.borders?.panel || "#cfd8dd",
  borderStrong: theme?.colors?.borders?.darkblue || "#b2c1c7",

  text: theme?.colors?.text?.primary || "#164D63",
  body: theme?.colors?.text?.body || "#3F5D6E",
  soft: theme?.colors?.text?.small || "#5C7A89",
  verySoft: theme?.colors?.text?.verysmall || "#7D97A1",

  corelia: theme?.colors?.text?.corelia || "#0e3f56",
  orange: theme?.colors?.text?.secondary || "#B8621B",
  green: theme?.colors?.text?.green || "#1A8C74",

  bluePastel: theme?.Generic?.blue?.pastel?.bg || "#E8F2F6",
  bluePastelText: theme?.Generic?.blue?.pastel?.text || "#1E5066",
  bluePastelBorder: theme?.Generic?.blue?.pastel?.border || "#B7C6CC",

  greenPastel: theme?.Generic?.green?.pastel?.bg || "#E7F3ED",
  greenPastelText: theme?.Generic?.green?.pastel?.text || "#166A58",
  greenPastelBorder: theme?.Generic?.green?.pastel?.border || "#B9CEC4",

  yellowPastel: theme?.Generic?.yellow?.pastel?.bg || "#F9F3E3",
  yellowPastelText: theme?.Generic?.yellow?.pastel?.text || "#8A4A13",
  yellowPastelBorder: theme?.Generic?.yellow?.pastel?.border || "#D8C6A6",

  greyPastel: theme?.Generic?.grey?.pastel?.bg || "#EDF1F4",
  greyPastelText: theme?.Generic?.grey?.pastel?.text || "#3F5D6E",
  greyPastelBorder: theme?.Generic?.grey?.pastel?.border || "#C7D0D6",
};

/* ============================================================================
   LIBRARIES
============================================================================ */

const LIBRARIES = [
  { id: "building", label: "Building Systems" },
  { id: "technical", label: "Technical Systems" },
  { id: "specific", label: "Specific Systems" },
];

const BUILDING_SYSTEMS = [
  "foundations",
  "structure",
  "floors",
  "roofing",
  "facades",
  "openings",
  "stairs",
  "partitions",
  "floor_finishes",
  "wall_finishes",
  "ceilings",
  "external_works",
];

const TECHNICAL_SYSTEMS = [
  "pressure",
  "gravity",
  "pumping",
  "storage",
  "hvac",
  "electrical",
  "automation",
  "process",
];

const SPECIFIC_SYSTEMS = [
  "water_tower",
  "culvert",
  "borehole",
  "solar_field",
];

/* ============================================================================
   MOCK ARTICLES
============================================================================ */

const DEFAULT_SPECIFICATION_ARTICLES = [
  {
    id: "spec-001",
    code: "CIV-01",
    title: "Classe de résistance du béton des ouvrages porteurs",
    family: "Building Systems",
    system: "structure",
    chapter: "Structure",
    kind: "Technique",
    status: "Consolidé",
    usageStatus: "Attribué",
    genericText:
      "Les structures en béton armé devront être conçues avec une classe de résistance adaptée aux sollicitations mécaniques, aux conditions d’exposition et aux objectifs de durabilité.",
    projectVariant:
      "Pour les ouvrages principaux du projet, la classe minimale retenue est C35/45.",
    projectNote:
      "Valeur à confirmer définitivement après calage géotechnique et hypothèses de durabilité.",
    usedBy: ["Réservoir principal 2000 m³", "Station de pompage SP1"],
    disciplines: ["Génie civil", "Structure"],
    systems: ["Structure"],
    interfaces: ["Hydraulique"],
  },
  {
    id: "spec-002",
    code: "CIV-02",
    title: "Traitement des fondations en zone sensible",
    family: "Building Systems",
    system: "foundations",
    chapter: "Fondations",
    kind: "Technique",
    status: "À confirmer",
    usageStatus: "Partiel",
    genericText:
      "Les fondations devront être adaptées à la nature des sols, aux tassements admissibles et aux conditions hydrogéologiques du site.",
    projectVariant:
      "Les hypothèses de fondations profondes ou semi-profondes restent à arbitrer sur les zones les plus compressibles.",
    projectNote:
      "La campagne géotechnique complémentaire conditionne la rédaction finale.",
    usedBy: ["Réservoir principal 2000 m³"],
    disciplines: ["Génie civil", "Géotechnique"],
    systems: ["Fondations"],
    interfaces: ["Structure"],
  },
  {
    id: "spec-003",
    code: "ARC-01",
    title: "Étanchéité de la toiture des bâtiments techniques",
    family: "Building Systems",
    system: "roofing",
    chapter: "Toitures",
    kind: "Technique",
    status: "Sous contrôle",
    usageStatus: "Attribué",
    genericText:
      "Les toitures devront garantir l’étanchéité durable de l’ouvrage, y compris au droit des relevés, pénétrations et points singuliers.",
    projectVariant:
      "Une membrane bicouche renforcée est retenue pour les locaux techniques sensibles.",
    projectNote: "",
    usedBy: ["Bâtiment technique BT1"],
    disciplines: ["Architecture"],
    systems: ["Toiture"],
    interfaces: ["Électricité", "Ventilation"],
  },
  {
    id: "spec-004",
    code: "HYD-01",
    title: "Pression minimale au point défavorisé",
    family: "Technical Systems",
    system: "pressure",
    chapter: "Performance hydraulique",
    kind: "Performance",
    status: "Consolidé",
    usageStatus: "Attribué",
    genericText:
      "La pression minimale au point défavorisé du réseau devra être maintenue à une valeur compatible avec la desserte normale des abonnés et la continuité du service.",
    projectVariant:
      "Pour le présent projet, la pression minimale retenue au point défavorisé est fixée à 2,0 bar en régime normal.",
    projectNote:
      "Valeur à confirmer après validation finale du modèle hydraulique.",
    usedBy: ["Réseau primaire Nord", "Réseau secondaire Messa"],
    disciplines: ["Hydraulique"],
    systems: ["Réseau de distribution"],
    interfaces: ["Pompage", "Stockage"],
  },
  {
    id: "spec-005",
    code: "AUT-01",
    title: "Alimentation secourue des automatismes",
    family: "Technical Systems",
    system: "automation",
    chapter: "Automatisme",
    kind: "Interface",
    status: "À confirmer",
    usageStatus: "Partiel",
    genericText:
      "Les automatismes assurant les fonctions critiques de l’installation devront être alimentés par une source secourue permettant le maintien des fonctions essentielles en cas de perte d’alimentation principale.",
    projectVariant:
      "Le principe d’un onduleur couplé à un groupe électrogène avec reprise automatique est retenu à ce stade.",
    projectNote:
      "L’autonomie minimale exigée et la hiérarchisation des charges secourues restent à arbitrer.",
    usedBy: ["Station de pompage SP1"],
    disciplines: ["Électricité", "Automatisme"],
    systems: ["Automatisme", "Secours énergie"],
    interfaces: ["Commande", "Supervision", "Pompage"],
  },
  {
    id: "spec-006",
    code: "SPA-01",
    title: "Capacité et stabilité du château d’eau",
    family: "Specific Systems",
    system: "water_tower",
    chapter: "Ouvrages spécifiques",
    kind: "Technique",
    status: "Consolidé",
    usageStatus: "Attribué",
    genericText:
      "Le château d’eau devra être dimensionné de manière à garantir la stabilité de l’ouvrage, la tenue au vent et la capacité utile retenue.",
    projectVariant:
      "Le château d’eau principal est retenu pour une capacité de 500 m³ avec vérification spécifique au vent.",
    projectNote: "",
    usedBy: ["Château d’eau CE1"],
    disciplines: ["Structure", "Hydraulique"],
    systems: ["Stockage élevé"],
    interfaces: ["Fondations"],
  },

  {
    id: "spec-007",
    code: "CIV-03",
    title: "Épaisseur minimale des dallages techniques",
    family: "Building Systems",
    system: "floors",
    chapter: "Sols",
    kind: "Technique",
    status: "Sous contrôle",
    usageStatus: "Attribué",
    genericText:
      "Les dallages techniques devront présenter une épaisseur compatible avec les charges roulantes, les efforts concentrés et la durabilité attendue en exploitation.",
    projectVariant:
      "Les zones de manutention recevront un dallage renforcé avec joints traités et finition adaptée.",
    projectNote:
      "À coordonner avec les hypothèses d’exploitation et les cheminements d’équipements.",
    usedBy: ["Station de pompage SP1", "Bâtiment électromécanique"],
    disciplines: ["Génie civil", "Exploitation"],
    systems: ["Dallage"],
    interfaces: ["Équipements"],
  },
  {
    id: "spec-008",
    code: "ARC-02",
    title: "Traitement des façades exposées",
    family: "Building Systems",
    system: "facades",
    chapter: "Façades",
    kind: "Technique",
    status: "À confirmer",
    usageStatus: "Non attribué",
    genericText:
      "Les façades exposées devront garantir la tenue aux intempéries, la limitation des infiltrations et une finition compatible avec le contexte du site.",
    projectVariant:
      "Une finition minérale hydrofuge est envisagée sur les faces les plus exposées au ruissellement.",
    projectNote:
      "Le choix définitif dépendra aussi des contraintes de maintenance locale.",
    usedBy: ["Bâtiment technique BT1"],
    disciplines: ["Architecture"],
    systems: ["Façades"],
    interfaces: ["Étanchéité"],
  },
  {
    id: "spec-009",
    code: "ARC-03",
    title: "Portes métalliques des locaux sensibles",
    family: "Building Systems",
    system: "openings",
    chapter: "Baies et accès",
    kind: "Fonctionnelle",
    status: "Consolidé",
    usageStatus: "Attribué",
    genericText:
      "Les portes métalliques des locaux sensibles devront assurer résistance mécanique, sécurité d’accès et tenue à l’ambiance du site.",
    projectVariant:
      "Les portes des locaux électriques seront galvanisées et équipées de ventilation maîtrisée.",
    projectNote: "",
    usedBy: ["Local TGBT", "Local commande"],
    disciplines: ["Architecture", "Électricité"],
    systems: ["Menuiseries métalliques"],
    interfaces: ["Sécurité", "Ventilation"],
  },
  {
    id: "spec-010",
    code: "ARC-04",
    title: "Escaliers d’accès aux zones d’exploitation",
    family: "Building Systems",
    system: "stairs",
    chapter: "Circulations",
    kind: "Technique",
    status: "Sous contrôle",
    usageStatus: "Partiel",
    genericText:
      "Les escaliers d’accès devront être dimensionnés pour un usage maintenance sécurisé et un entretien courant des installations.",
    projectVariant:
      "Les zones extérieures recevront des marches antidérapantes et garde-corps galvanisés.",
    projectNote:
      "À coordonner avec les hauteurs finales de plateforme.",
    usedBy: ["Station SP1", "Ouvrage de reprise"],
    disciplines: ["Architecture", "Génie civil"],
    systems: ["Accès maintenance"],
    interfaces: ["Sécurité"],
  },
  {
    id: "spec-011",
    code: "ARC-05",
    title: "Cloisons séparatives des locaux techniques",
    family: "Building Systems",
    system: "partitions",
    chapter: "Aménagement intérieur",
    kind: "Technique",
    status: "Consolidé",
    usageStatus: "Attribué",
    genericText:
      "Les cloisons séparatives devront permettre une sectorisation fonctionnelle claire et une maintenance adaptée des locaux techniques.",
    projectVariant:
      "Les cloisonnements internes seront réalisés en maçonnerie légère sur les zones permanentes.",
    projectNote: "",
    usedBy: ["Bâtiment exploitation"],
    disciplines: ["Architecture"],
    systems: ["Cloisons"],
    interfaces: ["Électricité"],
  },
  {
    id: "spec-012",
    code: "ARC-06",
    title: "Revêtement des sols des zones humides",
    family: "Building Systems",
    system: "floor_finishes",
    chapter: "Finitions",
    kind: "Technique",
    status: "À confirmer",
    usageStatus: "Partiel",
    genericText:
      "Les revêtements de sol des zones humides devront présenter une résistance adaptée aux projections, au nettoyage et aux circulations usuelles.",
    projectVariant:
      "Un carrelage antidérapant est prévu dans les locaux exposés aux lavages réguliers.",
    projectNote:
      "La classe de glissance doit être confirmée selon usage final.",
    usedBy: ["Local dosage", "Sanitaires"],
    disciplines: ["Architecture"],
    systems: ["Sols intérieurs"],
    interfaces: ["Nettoyage"],
  },
  {
    id: "spec-013",
    code: "ARC-07",
    title: "Protection des parois intérieures en ambiance agressive",
    family: "Building Systems",
    system: "wall_finishes",
    chapter: "Finitions murales",
    kind: "Performance",
    status: "Sous contrôle",
    usageStatus: "Attribué",
    genericText:
      "Les parois intérieures exposées à des ambiances agressives devront recevoir une protection assurant tenue chimique et facilité d’entretien.",
    projectVariant:
      "Une peinture époxy bicomposant est retenue dans les zones critiques de process.",
    projectNote: "",
    usedBy: ["Salle chlore", "Local réactifs"],
    disciplines: ["Architecture", "Process"],
    systems: ["Finitions murales"],
    interfaces: ["Hygiène", "Process"],
  },
  {
    id: "spec-014",
    code: "ARC-08",
    title: "Plafonds des zones techniques fermées",
    family: "Building Systems",
    system: "ceilings",
    chapter: "Plafonds",
    kind: "Fonctionnelle",
    status: "Non attribué",
    usageStatus: "Non attribué",
    genericText:
      "Les plafonds des zones techniques fermées devront permettre l’accès maintenance lorsque nécessaire sans compromettre la pérennité des locaux.",
    projectVariant:
      "Les plafonds démontables ne seront envisagés que dans les zones effectivement accessibles.",
    projectNote:
      "À confirmer après arbitrage d’exploitation.",
    usedBy: ["Local commande"],
    disciplines: ["Architecture"],
    systems: ["Plafonds"],
    interfaces: ["Maintenance"],
  },
  {
    id: "spec-015",
    code: "CIV-04",
    title: "Traitement des voiries et plateformes extérieures",
    family: "Building Systems",
    system: "external_works",
    chapter: "Aménagements extérieurs",
    kind: "Technique",
    status: "Consolidé",
    usageStatus: "Attribué",
    genericText:
      "Les voiries et plateformes extérieures devront être adaptées aux usages de maintenance, aux charges d’exploitation et aux conditions hydrauliques du site.",
    projectVariant:
      "Les voies d’accès principales recevront une structure renforcée pour poids lourds de maintenance.",
    projectNote: "",
    usedBy: ["Accès principal", "Plateforme réservoir"],
    disciplines: ["Génie civil", "VRD"],
    systems: ["Voiries"],
    interfaces: ["Drainage"],
  },

  {
    id: "spec-016",
    code: "HYD-02",
    title: "Gestion des régimes gravitaires",
    family: "Technical Systems",
    system: "gravity",
    chapter: "Hydraulique",
    kind: "Performance",
    status: "Sous contrôle",
    usageStatus: "Attribué",
    genericText:
      "Les tronçons gravitaires devront être conçus pour garantir l’écoulement normal sans mise en charge non maîtrisée.",
    projectVariant:
      "Les pentes minimales et organes de purge seront consolidés après validation altimétrique.",
    projectNote:
      "Coordination requise avec topographie et profils en long.",
    usedBy: ["Collecteur de reprise", "Conduite aval"],
    disciplines: ["Hydraulique"],
    systems: ["Écoulement gravitaire"],
    interfaces: ["Topographie", "GC"],
  },
  {
    id: "spec-017",
    code: "MEC-01",
    title: "Performance des groupes de pompage principaux",
    family: "Technical Systems",
    system: "pumping",
    chapter: "Pompage",
    kind: "Performance",
    status: "Consolidé",
    usageStatus: "Attribué",
    genericText:
      "Les groupes de pompage principaux devront satisfaire les débits de projet, les plages de fonctionnement et la continuité de service attendue.",
    projectVariant:
      "Une configuration N+1 est retenue sur les stations principales.",
    projectNote: "",
    usedBy: ["SP1", "SP2"],
    disciplines: ["Hydraulique", "Mécanique"],
    systems: ["Pompage"],
    interfaces: ["Électricité", "Automatisme"],
  },
  {
    id: "spec-018",
    code: "HYD-03",
    title: "Capacité utile des ouvrages de stockage",
    family: "Technical Systems",
    system: "storage",
    chapter: "Stockage",
    kind: "Technique",
    status: "À confirmer",
    usageStatus: "Partiel",
    genericText:
      "Les ouvrages de stockage devront présenter une capacité utile cohérente avec les hypothèses de régulation, d’autonomie et d’exploitation.",
    projectVariant:
      "Le volume utile de service reste à confirmer selon scénarios de distribution retenus.",
    projectNote:
      "À arbitrer avec le modèle hydraulique final.",
    usedBy: ["Réservoir R1", "Réservoir R2"],
    disciplines: ["Hydraulique", "Structure"],
    systems: ["Stockage"],
    interfaces: ["Exploitation"],
  },
  {
    id: "spec-019",
    code: "MEC-02",
    title: "Ventilation des locaux électromécaniques",
    family: "Technical Systems",
    system: "hvac",
    chapter: "Ambiances techniques",
    kind: "Fonctionnelle",
    status: "Sous contrôle",
    usageStatus: "Attribué",
    genericText:
      "Les locaux électromécaniques devront être ventilés de manière à maintenir des conditions compatibles avec la durée de vie des équipements.",
    projectVariant:
      "La ventilation naturelle assistée est privilégiée lorsque les charges thermiques le permettent.",
    projectNote: "",
    usedBy: ["Local GE", "Local TGBT"],
    disciplines: ["Mécanique", "Électricité"],
    systems: ["Ventilation technique"],
    interfaces: ["Architecture"],
  },
  {
    id: "spec-020",
    code: "ELE-01",
    title: "Sélectivité de la distribution électrique",
    family: "Technical Systems",
    system: "electrical",
    chapter: "Distribution électrique",
    kind: "Technique",
    status: "Consolidé",
    usageStatus: "Attribué",
    genericText:
      "La distribution électrique devra assurer une sélectivité adaptée permettant de limiter les pertes de service aux seuls départs concernés.",
    projectVariant:
      "Une étude de sélectivité sera exigée sur les niveaux principaux et départs critiques.",
    projectNote: "",
    usedBy: ["TGBT principal", "Armoires process"],
    disciplines: ["Électricité"],
    systems: ["Distribution BT"],
    interfaces: ["Automatisme", "Secours énergie"],
  },
  {
    id: "spec-021",
    code: "AUT-02",
    title: "Remontée d’états et supervision des organes critiques",
    family: "Technical Systems",
    system: "automation",
    chapter: "Automatisme",
    kind: "Interface",
    status: "Sous contrôle",
    usageStatus: "Attribué",
    genericText:
      "Les organes critiques devront faire l’objet d’une remontée d’état fiable vers le système de supervision avec gestion des défauts et acquittements.",
    projectVariant:
      "Les défauts prioritaires seront hiérarchisés selon criticité d’exploitation.",
    projectNote:
      "Liste finale des alarmes à stabiliser en phase suivante.",
    usedBy: ["SP1", "Réservoir R1"],
    disciplines: ["Automatisme", "Électricité"],
    systems: ["Supervision"],
    interfaces: ["Commande", "Télégestion"],
  },
  {
    id: "spec-022",
    code: "PRO-01",
    title: "Continuité de la chaîne de traitement",
    family: "Technical Systems",
    system: "process",
    chapter: "Process",
    kind: "Performance",
    status: "À confirmer",
    usageStatus: "Partiel",
    genericText:
      "La chaîne de traitement devra être conçue pour garantir la continuité du service selon les scénarios d’exploitation retenus.",
    projectVariant:
      "Les by-pass et modes dégradés restent à préciser pour certaines phases transitoires.",
    projectNote:
      "Arbitrage à poursuivre avec exploitation.",
    usedBy: ["Ligne traitement 1", "Ligne traitement 2"],
    disciplines: ["Process", "Hydraulique"],
    systems: ["Traitement"],
    interfaces: ["Automatisme", "Électricité"],
  },
  {
    id: "spec-023",
    code: "HYD-04",
    title: "Protection contre les surpressions transitoires",
    family: "Technical Systems",
    system: "pressure",
    chapter: "Protection réseau",
    kind: "Technique",
    status: "Sous contrôle",
    usageStatus: "Attribué",
    genericText:
      "Le système devra intégrer des dispositions limitant les surpressions transitoires préjudiciables aux conduites et organes.",
    projectVariant:
      "Des dispositifs anti-bélier seront étudiés sur les tronçons identifiés comme sensibles.",
    projectNote:
      "À confirmer après analyse transitoire.",
    usedBy: ["Conduite refoulement SP2"],
    disciplines: ["Hydraulique", "Mécanique"],
    systems: ["Protection anti-bélier"],
    interfaces: ["Pompage"],
  },
  {
    id: "spec-024",
    code: "ELE-02",
    title: "Mise à la terre des équipements critiques",
    family: "Technical Systems",
    system: "electrical",
    chapter: "Sécurité électrique",
    kind: "Technique",
    status: "Consolidé",
    usageStatus: "Attribué",
    genericText:
      "Les équipements critiques devront être intégrés à un schéma de mise à la terre conforme aux objectifs de sécurité et de continuité.",
    projectVariant:
      "Le maillage de terre sera renforcé au droit des bâtiments techniques et zones process.",
    projectNote: "",
    usedBy: ["TGBT", "GE", "Armoires process"],
    disciplines: ["Électricité"],
    systems: ["Mise à la terre"],
    interfaces: ["GC"],
  },

  {
    id: "spec-025",
    code: "SPA-02",
    title: "Franchissement hydraulique par dalot",
    family: "Specific Systems",
    system: "culvert",
    chapter: "Ouvrages spécifiques",
    kind: "Technique",
    status: "À confirmer",
    usageStatus: "Partiel",
    genericText:
      "Le dalot de franchissement devra garantir le passage hydraulique compatible avec les conditions de site et de maintenance.",
    projectVariant:
      "La section utile sera ajustée selon les données hydrauliques de ruissellement finalisées.",
    projectNote:
      "À confirmer après validation hydrologique.",
    usedBy: ["Franchissement accès Sud"],
    disciplines: ["Génie civil", "Hydraulique"],
    systems: ["Ouvrage de franchissement"],
    interfaces: ["Voiries"],
  },
  {
    id: "spec-026",
    code: "SPA-03",
    title: "Équipement et sécurité des forages",
    family: "Specific Systems",
    system: "borehole",
    chapter: "Captage",
    kind: "Fonctionnelle",
    status: "Sous contrôle",
    usageStatus: "Attribué",
    genericText:
      "Les forages devront être équipés de manière à assurer sécurité, pérennité et accès maintenance des organes de captage.",
    projectVariant:
      "Les têtes de forage recevront une protection renforcée et dispositif de verrouillage.",
    projectNote: "",
    usedBy: ["Forage F1", "Forage F2"],
    disciplines: ["Hydraulique", "Mécanique"],
    systems: ["Captage"],
    interfaces: ["Électricité", "Sécurité"],
  },
  {
    id: "spec-027",
    code: "SPA-04",
    title: "Implantation et protection du champ solaire",
    family: "Specific Systems",
    system: "solar_field",
    chapter: "Énergie spécifique",
    kind: "Technique",
    status: "À confirmer",
    usageStatus: "Non attribué",
    genericText:
      "Le champ solaire devra être implanté de manière à garantir performance, sécurité, accessibilité maintenance et compatibilité avec le site.",
    projectVariant:
      "Une clôture périphérique et un drainage léger sont envisagés autour de l’emprise.",
    projectNote:
      "À stabiliser après étude d’implantation détaillée.",
    usedBy: ["Zone énergie solaire"],
    disciplines: ["Électricité", "Génie civil"],
    systems: ["Production solaire"],
    interfaces: ["Sécurité", "VRD"],
  },
  {
    id: "spec-028",
    code: "SPA-05",
    title: "Accès maintenance du château d’eau",
    family: "Specific Systems",
    system: "water_tower",
    chapter: "Ouvrages spécifiques",
    kind: "Fonctionnelle",
    status: "Sous contrôle",
    usageStatus: "Attribué",
    genericText:
      "Le château d’eau devra comporter des accès maintenance compatibles avec l’exploitation et les interventions périodiques.",
    projectVariant:
      "L’échelle et les paliers de repos seront dimensionnés selon hauteur finale retenue.",
    projectNote: "",
    usedBy: ["Château d’eau CE1"],
    disciplines: ["Structure", "Exploitation"],
    systems: ["Accès maintenance"],
    interfaces: ["Sécurité"],
  },
];

/* ============================================================================
   HELPERS
============================================================================ */

const getLibrarySystems = (libraryId) => {
  if (libraryId === "building") return BUILDING_SYSTEMS;
  if (libraryId === "technical") return TECHNICAL_SYSTEMS;
  if (libraryId === "specific") return SPECIFIC_SYSTEMS;
  return [];
};

const getLibraryLabelFromSystem = (system) => {
  if (BUILDING_SYSTEMS.includes(system)) return "building";
  if (TECHNICAL_SYSTEMS.includes(system)) return "technical";
  if (SPECIFIC_SYSTEMS.includes(system)) return "specific";
  return "building";
};

const getSystemCount = (articles, system) =>
  articles.filter((item) => item.system === system).length;

const getSystemAssignedCount = (articles, system) =>
  articles.filter(
    (item) =>
      item.system === system &&
      String(item.usageStatus || "").toLowerCase() === "attribué"
  ).length;

const getUsageStyle = (value) => {
  const v = String(value || "").toLowerCase();

  if (v === "attribué") {
    return {
      bg: T.greenPastel,
      text: T.greenPastelText,
      border: T.greenPastelBorder,
      label: "Attribué",
    };
  }

  if (v === "partiel") {
    return {
      bg: T.yellowPastel,
      text: T.yellowPastelText,
      border: T.yellowPastelBorder,
      label: "Partiel",
    };
  }

  return {
    bg: T.greyPastel,
    text: T.greyPastelText,
    border: T.greyPastelBorder,
    label: "Non attribué",
  };
};

const getStatusStyle = (value) => {
  const v = String(value || "").toLowerCase();

  if (v === "consolidé") {
    return {
      bg: T.greenPastel,
      text: T.greenPastelText,
      border: T.greenPastelBorder,
    };
  }

  if (v === "sous contrôle") {
    return {
      bg: T.bluePastel,
      text: T.bluePastelText,
      border: T.bluePastelBorder,
    };
  }

  return {
    bg: T.yellowPastel,
    text: T.yellowPastelText,
    border: T.yellowPastelBorder,
  };
};

const formatSystemLabel = (value) => {
  if (!value) return "";
  return value
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const getSymbolColor = () => T.corelia;

const renderSystemSymbol = (libraryId, family) => {
  const color = getSymbolColor();

  if (libraryId === "building") {
    return <BuildingSystemsSymbol family={family} color={color} />;
  }

  if (libraryId === "technical") {
    return <TechnicalSystemsSymbol family={family} color={color} />;
  }

  if (libraryId === "specific") {
    return <SpecificAssetsSymbol family={family} color={color} />;
  }

  return null;
};

const emptyArticle = (libraryId, systemId) => ({
  id: null,
  code: "",
  title: "",
  family:
    libraryId === "building"
      ? "Building Systems"
      : libraryId === "technical"
      ? "Technical Systems"
      : "Specific Systems",
  system: systemId || "",
  chapter: "",
  kind: "Technique",
  status: "À confirmer",
  usageStatus: "Non attribué",
  genericText: "",
  projectVariant: "",
  projectNote: "",
  usedBy: [],
  disciplines: [],
  systems: [],
  interfaces: [],
});

/* ============================================================================
   UI ATOMS
============================================================================ */

const SectionTitle = ({ children }) => (
  <div
    style={{
      fontFamily: theme?.fonts?.body || "inherit",
      fontSize: "0.84rem",
      fontWeight: theme?.fontWeights?.medium || 500,
      lineHeight: 1.18,
      color: T.soft,
      marginBottom: 8,
    }}
  >
    {children}
  </div>
);

const FilterPill = ({ label, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      height: 30,
      padding: "0 12px",
      borderRadius: theme?.borders?.radius?.full || 9999,
      border: `1px solid ${active ? T.borderStrong : T.border}`,
      background: active ? T.bluePastel : "#ffffff",
      color: active ? T.corelia : T.body,
      fontFamily: theme?.fonts?.body || "inherit",
      fontSize: "0.74rem",
      fontWeight: 500,
      lineHeight: 1,
      whiteSpace: "nowrap",
      cursor: "pointer",
    }}
  >
    {label}
  </button>
);

const MicroTag = ({ label, bg, text, border }) => (
  <div
    style={{
      display: "inline-flex",
      alignItems: "center",
      minHeight: 24,
      padding: "0 8px",
      borderRadius: theme?.borders?.radius?.full || 9999,
      background: bg,
      color: text,
      border: `1px solid ${border || "transparent"}`,
      fontFamily: theme?.fonts?.body || "inherit",
      fontSize: "0.68rem",
      lineHeight: 1,
      fontWeight: 500,
      whiteSpace: "nowrap",
      boxSizing: "border-box",
    }}
  >
    {label}
  </div>
);

const QuietStat = ({ value, label }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      gap: 2,
      minWidth: 70,
    }}
  >
    <div
      style={{
        fontFamily: theme?.fonts?.body || "inherit",
        fontSize: "0.96rem",
        lineHeight: 1,
        color: T.text,
        fontWeight: 600,
      }}
    >
      {value}
    </div>
    <div
      style={{
        fontFamily: theme?.fonts?.body || "inherit",
        fontSize: "0.65rem",
        lineHeight: 1.2,
        color: T.verySoft,
      }}
    >
      {label}
    </div>
  </div>
);

const FieldLabel = ({ children }) => (
  <div
    style={{
      fontFamily: theme?.fonts?.body || "inherit",
      fontSize: "0.71rem",
      lineHeight: 1.1,
      color: T.soft,
      marginBottom: 5,
    }}
  >
    {children}
  </div>
);

const BaseInput = (props) => (
  <input
    {...props}
    style={{
      width: "100%",
      minHeight: 31,
      border: theme?.form?.field?.border || `1px solid ${T.border}`,
      borderRadius: theme?.form?.field?.borderRadius || 3,
      background: "#ffffff",
      color: T.body,
      fontFamily: theme?.fonts?.body || "inherit",
      fontSize: "0.78rem",
      boxSizing: "border-box",
      padding: "0 9px",
      ...(props.style || {}),
    }}
  />
);

const BaseSelect = (props) => (
  <select
    {...props}
    style={{
      width: "100%",
      minHeight: 31,
      border: theme?.form?.field?.border || `1px solid ${T.border}`,
      borderRadius: theme?.form?.field?.borderRadius || 3,
      background: "#ffffff",
      color: T.body,
      fontFamily: theme?.fonts?.body || "inherit",
      fontSize: "0.78rem",
      boxSizing: "border-box",
      padding: "0 9px",
      ...(props.style || {}),
    }}
  />
);

const BaseTextarea = (props) => (
  <textarea
    {...props}
    style={{
      width: "100%",
      minHeight: 88,
      border: theme?.form?.field?.border || `1px solid ${T.border}`,
      borderRadius: theme?.form?.field?.borderRadius || 3,
      background: "#ffffff",
      color: T.body,
      fontFamily: theme?.fonts?.body || "inherit",
      fontSize: "0.78rem",
      boxSizing: "border-box",
      padding: "9px 10px",
      resize: "vertical",
      lineHeight: 1.45,
      ...(props.style || {}),
    }}
  />
);

const ActionButton = ({ label, onClick, primary = false, danger = false }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      minHeight: 32,
      padding: "0 10px",
      borderRadius: theme?.borders?.radius?.md || 4,
      border: `1px solid ${
        danger
          ? T.yellowPastelBorder
          : primary
          ? T.bluePastelBorder
          : T.border
      }`,
      background: danger ? T.yellowPastel : primary ? T.bluePastel : "#ffffff",
      color: danger ? T.yellowPastelText : primary ? T.corelia : T.body,
      fontFamily: theme?.fonts?.body || "inherit",
      fontSize: "0.75rem",
      fontWeight: 500,
      lineHeight: 1.1,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxSizing: "border-box",
      whiteSpace: "nowrap",
    }}
  >
    {label}
  </button>
);
const REQUIREMENT_TYPES = [
  { value: "Technique", label: "Technique" },
  { value: "Performance", label: "Performance" },
  { value: "Interface", label: "Interface" },
  { value: "Fonctionnelle", label: "Fonctionnelle" },
  { value: "Exploitation", label: "Exploitation" },
];
/* ============================================================================
   COMPONENT
============================================================================ */

const SpecificationBuilderTab = ({
  specificationArticles = DEFAULT_SPECIFICATION_ARTICLES,
  onSelectItem,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [usageFilter, setUsageFilter] = useState("ALL");
  const [activeLibrary, setActiveLibrary] = useState("building");
  const [activeSystem, setActiveSystem] = useState("structure");
  const [selectedArticleId, setSelectedArticleId] = useState(null);

  const [drafts, setDrafts] = useState(() =>
    specificationArticles.reduce((acc, item) => {
      acc[item.id] = { ...item };
      return acc;
    }, {})
  );

  const [draftArticle, setDraftArticle] = useState(null);

  useEffect(() => {
    const allowedSystems = getLibrarySystems(activeLibrary);
    if (!allowedSystems.includes(activeSystem)) {
      setActiveSystem(allowedSystems[0] || null);
    }
  }, [activeLibrary, activeSystem]);

  const systems = useMemo(() => getLibrarySystems(activeLibrary), [activeLibrary]);

  const mergedArticles = useMemo(() => {
    return specificationArticles.map((item) => ({
      ...item,
      ...(drafts[item.id] || {}),
    }));
  }, [specificationArticles, drafts]);

  const libraryArticles = useMemo(() => {
    return mergedArticles.filter(
      (item) => getLibraryLabelFromSystem(item.system) === activeLibrary
    );
  }, [mergedArticles, activeLibrary]);

  const filteredArticles = useMemo(() => {
    return libraryArticles.filter((item) => {
      const systemMatch = !activeSystem || item.system === activeSystem;

      const usageMatch =
        usageFilter === "ALL"
          ? true
          : String(item.usageStatus || "").toLowerCase() === usageFilter.toLowerCase();

      const text = [
        item.code,
        item.title,
        item.chapter,
        item.family,
        item.kind,
      ]
        .join(" ")
        .toLowerCase();

      const searchMatch =
        !searchTerm.trim() || text.includes(searchTerm.trim().toLowerCase());

      return systemMatch && usageMatch && searchMatch;
    });
  }, [libraryArticles, activeSystem, usageFilter, searchTerm]);

  const selectedArticle = useMemo(() => {
    if (draftArticle) return draftArticle;

    return (
      filteredArticles.find((item) => item.id === selectedArticleId) ||
      filteredArticles[0] ||
      null
    );
  }, [filteredArticles, selectedArticleId, draftArticle]);

  useEffect(() => {
    if (selectedArticle && !draftArticle) {
      onSelectItem?.(selectedArticle);
    }
  }, [selectedArticle, onSelectItem, draftArticle]);

  const summary = useMemo(() => {
    return {
      systems: systems.length,
      articles: libraryArticles.length,
      visible: filteredArticles.length,
      assigned: libraryArticles.filter(
        (item) => String(item.usageStatus || "").toLowerCase() === "attribué"
      ).length,
    };
  }, [systems, libraryArticles, filteredArticles]);

  const currentArticle = selectedArticle || emptyArticle(activeLibrary, activeSystem);

  const updateCurrentArticle = (field, value) => {
    if (draftArticle) {
      setDraftArticle((prev) => ({
        ...prev,
        [field]: value,
      }));
      return;
    }

    if (!currentArticle?.id) return;

    setDrafts((prev) => ({
      ...prev,
      [currentArticle.id]: {
        ...(prev[currentArticle.id] || currentArticle),
        [field]: value,
      },
    }));
  };

  const handleSelectExistingArticle = (item) => {
    setDraftArticle(null);
    setSelectedArticleId(item.id);
    onSelectItem?.(item);
  };

  const handleCreateNew = () => {
    const fresh = emptyArticle(activeLibrary, activeSystem);
    setDraftArticle(fresh);
  };

  const handleDuplicate = () => {
    const source = currentArticle;
    const duplicated = {
      ...source,
      id: null,
      code: "",
      title: `${source.title || ""} — copie`.trim(),
      usageStatus: "Non attribué",
    };
    setDraftArticle(duplicated);
  };

  const handleSave = () => {
    if (draftArticle) {
      const newId = `draft-${Date.now()}`;
      const newItem = { ...draftArticle, id: newId };

      setDrafts((prev) => ({
        ...prev,
        [newId]: newItem,
      }));

      setDraftArticle(null);
      setSelectedArticleId(newId);
      onSelectItem?.(newItem);
      return;
    }

    if (currentArticle?.id) {
      onSelectItem?.(currentArticle);
    }
  };

  const handleReset = () => {
    if (draftArticle) {
      setDraftArticle(emptyArticle(activeLibrary, activeSystem));
      return;
    }

    const source = specificationArticles.find((item) => item.id === currentArticle?.id);
    if (!source) return;

    setDrafts((prev) => ({
      ...prev,
      [currentArticle.id]: { ...source },
    }));
  };

  const handleCancelNew = () => {
    setDraftArticle(null);
  };

  const usageStyle = getUsageStyle(currentArticle.usageStatus);
  const statusStyle = getStatusStyle(currentArticle.status);
  const REQUIREMENT_TYPES = [
  { value: "Technique", label: "Technique" },
  { value: "Performance", label: "Performance" },
  { value: "Interface", label: "Interface" },
  { value: "Fonctionnelle", label: "Fonctionnelle" },
  { value: "Exploitation", label: "Exploitation" },
];
  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 18,
        boxSizing: "border-box",
      }}
    >
      {/* ============================================================
         1. LIBRARIES + PILOTAGE LÉGER
      ============================================================ */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr auto",
          gap: 16,
          alignItems: "end",
        }}
      >
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 6,
            alignItems: "center",
          }}
        >
          {LIBRARIES.map((library) => (
            <FilterPill
              key={library.id}
              label={library.label}
              active={activeLibrary === library.id}
              onClick={() => setActiveLibrary(library.id)}
            />
          ))}
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 18,
            flexWrap: "wrap",
            justifyContent: "flex-end",
          }}
        >
          <QuietStat value={summary.systems} label="systèmes" />
          <QuietStat value={summary.articles} label="articles" />
          <QuietStat value={summary.visible} label="visibles" />
          <QuietStat value={summary.assigned} label="attribués" />
        </div>
      </div>

      {/* ============================================================
         2. STRUCTURE PRINCIPALE
         ------------------------------------------------------------
         Colonne gauche : cartes systèmes sur toute la hauteur
         Colonne droite : haut = articles / bas = édition
      ============================================================ */}
      <div
        style={{
          width: "100%",
          display: "grid",
          gridTemplateColumns: "300px minmax(0, 1fr)",
          gap: 18,
          alignItems: "stretch",
        }}
      >
        {/* --------------------------------------------------------
           2A. COLONNE GAUCHE — SYSTÈMES
        -------------------------------------------------------- */}
        <div
          style={{
            width: "100%",
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: 10,
            alignSelf: "stretch",
          }}
        >
          <SectionTitle>Systèmes</SectionTitle>

          <div
            style={{
              flex: 1,
              minHeight: 0,
              boxSizing: "border-box",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 10,
                alignContent: "start",
              }}
            >
              {systems.map((system) => {
                const isActive = activeSystem === system;
                const totalCount = getSystemCount(libraryArticles, system);
                const assignedCount = getSystemAssignedCount(libraryArticles, system);

                return (
                  <button
                    key={system}
                    type="button"
                    onClick={() => setActiveSystem(system)}
                    style={{
                      padding: "10px 12px 10px 12px",
                      borderRadius: theme?.borders?.radius?.lg || 6,
                      border: `1px solid ${isActive ? T.borderStrong : T.border}`,
                      background: isActive ? T.hover : "#ffffff",
                      cursor: "pointer",
                      display: "flex",
                      flexDirection: "column",
                      gap: 8,
                      minHeight: 82,
                      boxSizing: "border-box",
                      textAlign: "left",
                    }}
                  >
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "34px 1fr",
                        gap: 10,
                        alignItems: "start",
                      }}
                    >
                      <div
                        style={{
                          width: 34,
                          height: 34,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: 6,
                          border: `1px solid ${isActive ? T.borderStrong : T.border}`,
                          background: "#ffffff",
                          flexShrink: 0,
                        }}
                      >
                        {renderSystemSymbol(activeLibrary, system)}
                      </div>

                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-end",
                          gap: 2,
                          minWidth: 0,
                        }}
                      >
                        <div
                          style={{
                            fontFamily: theme?.fonts?.body || "inherit",
                            fontSize: "0.67rem",
                            lineHeight: 1.1,
                            color: T.verySoft,
                            textAlign: "right",
                          }}
                        >
                          {totalCount} article{totalCount > 1 ? "s" : ""}
                        </div>

                        <div
                          style={{
                            fontFamily: theme?.fonts?.body || "inherit",
                            fontSize: "0.67rem",
                            lineHeight: 1.1,
                            color: assignedCount > 0 ? T.green : T.verySoft,
                            textAlign: "right",
                          }}
                        >
                          {assignedCount} attrib.
                        </div>
                      </div>
                    </div>

                    <div
                      style={{
                        fontFamily: theme?.fonts?.body || "inherit",
                        fontSize: "0.76rem",
                        lineHeight: 1.2,
                        color: T.text,
                        fontWeight: 500,
                      }}
                    >
                      {formatSystemLabel(system)}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* --------------------------------------------------------
           2B. COLONNE DROITE — ARTICLES + ÉDITION
        -------------------------------------------------------- */}
        <div
          style={{
            width: "100%",
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: 16,
          }}
        >
          {/* ------------------------------------------------------
             2B-1. ARTICLES SÉLECTIONNÉS
          ------------------------------------------------------ */}
          <div
            style={{
              width: "100%",
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              gap: 10,
            }}
          >
<div
  style={{
    display: "grid",
    gridTemplateColumns: "400px 300px 110px",
    gap: 24,
    alignItems: "start",
    width: "100%",
  }}
>
  <div style={{ minWidth: 0 }}>
    <TextFilter
      label="Recherche"
      labelWidth={90}
      align="center"
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
      placeholder="Code, intitulé..."
      width="100%"
      inputStyle={{
        fontSize: "0.82rem",
        minHeight: 30,
        padding: "2px 8px 2px 32px",
      }}
    />
  </div>

  <div
    style={{
      minWidth: 0,
      marginTop: 0,
    }}
  >
    <DropdownList
      label="Usage"
      labelWidth={70}
      align="center"
      value={usageFilter}
      onChange={setUsageFilter}
      options={[
        { value: "ALL", label: "Tous" },
        { value: "attribué", label: "Attribués" },
        { value: "partiel", label: "Partiels" },
        { value: "non attribué", label: "Non attribués" },
      ]}
      width="100%"
      inputMinHeight={30}

      inputStyle={{
        fontSize: "0.82rem",
        minHeight: 30,
        padding: "2px 8px 2px 32px",
      }}
      style={{
        fontSize: "0.82rem",
        padding: "0 10px",
      }}
    />
  </div>

  <div
    style={{
      minWidth: 0,
      paddingTop: 8,
    }}
  >
    <Button
      label=" "
      variant="pastelGreen"
      onClick={handleCreateNew}
      width="100%"
      inputStyle={{
        fontSize: "0.82rem",
        minHeight: 30,
        padding: "2px 8px 2px 32px",
      }}
      style={{
        minHeight: 30,
        fontSize: "0.82rem",
        padding: "0 10px",
      }}
    >
      Nouveau
    </Button>
  </div>
</div>
            <SectionTitle>
              Articles sélectionnés — {formatSystemLabel(activeSystem)}
            </SectionTitle>

            <div
              style={{
                border: `1px solid ${T.border}`,
                background: T.card,
                minHeight: 240,
                maxHeight: 280,
                overflowY: "auto",
                overflowX: "hidden",
                padding: 10,
                boxSizing: "border-box",
              }}
            >
              {filteredArticles.length === 0 ? (
                <div
                  style={{
                    minHeight: 140,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    padding: 20,
                    boxSizing: "border-box",
                  }}
                >
                  <div
                    style={{
                      maxWidth: 280,
                      fontFamily: theme?.fonts?.body || "inherit",
                      fontSize: "0.82rem",
                      lineHeight: 1.45,
                      color: T.soft,
                    }}
                  >
                    Aucun article ne correspond au système et aux filtres actifs.
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                    gap: 8,
                    alignItems: "start",
                    width: "100%",
                  }}
                >
                  {filteredArticles.map((item) => {
                    const isSelected = !draftArticle && currentArticle?.id === item.id;
                    const itemUsageStyle = getUsageStyle(item.usageStatus);
                    const itemStatusStyle = getStatusStyle(item.status);

                    return (
                      <button
                    key={item.id}
                    type="button"
                    onClick={() => handleSelectExistingArticle(item)}
                    style={{
                        position: "relative",
                        overflow: "visible",

                        width: "100%",
                        minWidth: 0,
                        display: "grid",
                        gridTemplateColumns: "minmax(0, 1fr)",
                        gap: 10,
                        alignItems: "stretch",
                        border: `1px solid ${isSelected ? T.borderStrong : T.border}`,
                        background: "#ffffff",
                        cursor: "pointer",
                        textAlign: "left",
                        padding: "10px 10px 10px 0",
                        boxSizing: "border-box",
                    }}
                    >
                    {/* PIN — affichée uniquement sur l’article sélectionné */}
                    {isSelected && (
                        <PinRepere
                    letter="S"
                    variant="pastelOrange"
                    size={24}
                    style={{
                        position: "absolute",
                        top: 8,
                        left: 5,
                        zIndex: 2,

                        backgroundColor: T.orange,   // cercle plein
                        border: "none",              // pas de contour
                        color: "#ffffff",            // lettre blanche
                    }}
                    />
                    )}

                    {/* <div
                        style={{
                        background:
                            String(item.usageStatus || "").toLowerCase() === "attribué"
                            ? T.green
                            : String(item.usageStatus || "").toLowerCase() === "partiel"
                            ? T.orange
                            : T.borderStrong,
                        }}
                    /> */}

                    <div
                        style={{
                        minWidth: 0,
                        display: "flex",
                        flexDirection: "column",
                        gap: 7,
                        paddingLeft: isSelected ? 36 : 0,
                        }}
                    >
                        <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "1fr auto",
                            gap: 10,
                            alignItems: "start",
                        }}
                        >
                        <div
                            style={{
                            minWidth: 0,
                            display: "flex",
                            flexDirection: "column",
                            gap: 3,
                            }}
                        >
                            <div
                            style={{
                                fontFamily: theme?.fonts?.body || "inherit",
                                fontSize: "0.69rem",
                                lineHeight: 1.1,
                                color: T.verySoft,
                            }}
                            >
                            {item.code || "—"} · {item.chapter || "Général"}
                            </div>

                            <div
                            style={{
                                fontFamily: theme?.fonts?.body || "inherit",
                                fontSize: "0.80rem",
                                lineHeight: 1.28,
                                color: T.text,
                                fontWeight: 500,
                            }}
                            >
                            {item.title || "Article sans titre"}
                            </div>
                        </div>

                        <MicroTag
                            label={item.kind || "—"}
                            bg={T.greyPastel}
                            text={T.greyPastelText}
                            border={T.greyPastelBorder}
                        />
                        </div>

                        <div
                        style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 6,
                            alignItems: "center",
                        }}
                        >
                        <MicroTag
                            label={itemUsageStyle.label}
                            bg={itemUsageStyle.bg}
                            text={itemUsageStyle.text}
                            border={itemUsageStyle.border}
                        />

                        <MicroTag
                            label={item.status || "—"}
                            bg={itemStatusStyle.bg}
                            text={itemStatusStyle.text}
                            border={itemStatusStyle.border}
                        />
                        </div>
                    </div>
                    </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

{/* ------------------------------------------------------
   2B-2. ÉDITION DE L’ARTICLE
------------------------------------------------------ */}
<div
  style={{
    width: "100%",
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 8,
  }}
>
  <SectionTitle>
    {draftArticle ? "Création / variante" : "Édition de l’article"}
  </SectionTitle>

  <div
    style={{
      //border: `1px solid ${T.border}`,
      //background: "#ffffff",
      minHeight: 340,
      maxHeight: 460,
      overflowY: "auto",
      padding: 14,
      display: "flex",
      flexDirection: "column",
      gap: 12,
    }}
  >
    {/* 1. LIGNE PRINCIPALE — CHAMPS + TAGS */}
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "0.9fr auto",
        gap: 8,
        alignItems: "start",
      }}
    >
      {/* COLONNE GAUCHE — CHAMPS */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "130px minmax(0, 1fr)",
          gap: 10,
          alignItems: "end",
        }}
      >
        <div>
          <FieldLabel>Code</FieldLabel>
          <BaseInput
            value={currentArticle.code || ""}
            disabled
          />
        </div>

        <div>
          <FieldLabel>Intitulé</FieldLabel>
          <BaseInput
            value={currentArticle.title || ""}
            onChange={(e) => updateCurrentArticle("title", e.target.value)}
          />
        </div>
      </div>

      {/* COLONNE DROITE — TAGS */}
      <div
        style={{
          display: "flex",
          gap: 4,
          alignItems: "flex-start",
          justifyContent: "flex-end",
          flexWrap: "wrap",
          alignSelf: "start",
        }}
      >
        {currentArticle.disciplines?.[0] && (
          <MicroTag
            label={currentArticle.disciplines[0]}
            bg={T.greyPastel}
            text={T.greyPastelText}
            border={T.greyPastelBorder}
          />
        )}

        {(currentArticle.systems?.[0] || currentArticle.system) && (
          <MicroTag
            label={
              currentArticle.systems?.[0] ||
              formatSystemLabel(currentArticle.system)
            }
            bg={T.bluePastel}
            text={T.bluePastelText}
            border={T.bluePastelBorder}
          />
        )}
      </div>
    </div>

    {/* 2. TYPE D’EXIGENCE */}
    <RadioGroup
  label="Type d’exigence (*)"
  name="requirementType"
  options={REQUIREMENT_TYPES}
  value={currentArticle.kind || ""}
  onChange={(val) => updateCurrentArticle("kind", val)}
  orientation="horizontal"
  labelWidth={150}
  compact={true}
  width="80%"
  justify="space-between"
  colorMap={theme.requirementTypes}
  style={{ marginBottom: 6, gap: 0, }}
/>

    {/* 3. EXIGENCES — STRUCTURE OPTIMISÉE */}
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 10,
        alignItems: "start",
        padding : 10,
      }}
    >
      {/* COLONNE GAUCHE */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <div>
          <FieldLabel>Exigence de référence (*)</FieldLabel>
          <BaseTextarea
            value={currentArticle.genericText || ""}
            onChange={(e) =>
              updateCurrentArticle("genericText", e.target.value)
            }
            style={{ minHeight: 75 }}
          />
        </div>

        <div>
          <FieldLabel>Commentaire technique</FieldLabel>
          <BaseTextarea
            value={currentArticle.projectNote || ""}
            onChange={(e) =>
              updateCurrentArticle("projectNote", e.target.value)
            }
            style={{ minHeight: 75 }}
          />
        </div>
      </div>

      {/* COLONNE DROITE */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
        <div>
          <FieldLabel>Exigence retenue (*)</FieldLabel>
          <BaseTextarea
            value={currentArticle.projectVariant || ""}
            onChange={(e) =>
              updateCurrentArticle("projectVariant", e.target.value)
            }
            style={{ minHeight: 75 }}
          />
        </div>

        {/* CONTENEUR INVISIBLE — ACTIONS */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 16,
            alignItems: "stretch",
            minWidth: 0,
            marginTop: 12, // ← AJOUT
            padding:16,          }}
        >
          <div style={{ minWidth: 0 }}>
            <Button
              variant="pastelRed"
              width="100%"
              onClick={handleDuplicate}
              style={{
                width: "100%",
                minWidth: 0,
                minHeight: 28,
                padding: "0 6px",
                fontSize: "0.72rem",
                whiteSpace: "normal",
                textAlign: "center",
              }}
            >
              Dupliquer
            </Button>
          </div>

          <div style={{ minWidth: 0 }}>
            <Button
              variant="pastelGrey"
              width="100%"
              onClick={handleReset}
              style={{
                width: "100%",
                minWidth: 0,
                minHeight: 28,
                padding: "0 6px",
                fontSize: "0.72rem",
                whiteSpace: "normal",
                textAlign: "center",
              }}
            >
              Réinitialiser
            </Button>
          </div>

          <div style={{ minWidth: 0 }}>
            <Button
              variant="pastelYellow"
              width="100%"
              onClick={handleCreateNew}
              style={{
                width: "100%",
                minWidth: 0,
                minHeight: 28,
                padding: "0 6px",
                fontSize: "0.72rem",
                whiteSpace: "normal",
                textAlign: "center",
              }}
            >
              Nouveau
            </Button>
          </div>

          <div style={{ minWidth: 0 }}>
            <Button
              variant="pastelBlue"
              width="100%"
              onClick={handleSave}
              style={{
                width: "100%",
                minWidth: 0,
                minHeight: 28,
                padding: "0 6px",
                fontSize: "0.72rem",
                whiteSpace: "normal",
                textAlign: "center",
              }}
            >
              Enregistrer
            </Button>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
        </div>
      </div>
    </div>
  );
};

export default SpecificationBuilderTab;